import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Clock, 
  Target, 
  CheckCircle, 
  XCircle, 
  AlertCircle, 
  ArrowLeft, 
  ArrowRight, 
  Play, 
  BarChart3, 
  TrendingUp, 
  Calendar, 
  Award, 
  RefreshCw, 
  Save,
  Camera,
  Shield,
  AlertTriangle,
  Loader2
} from 'lucide-react';


import { useUserData } from '../../contexts/UserDataContext';
import { useTest } from '../../contexts/TestContext';
import { useRealtime } from '../../contexts/RealtimeContext';
import { saveTestResult, getUserWrongQuestions, getQuestionsFromBank, getQuestionsFromTest, getMockTest, saveWrongQuestion, getTestById, getTestResultsByTestId, saveTestStatistics, storeTestResultWithAnalytics, getTestPerformanceComparison, updateUserProfile } from '../../firebase/collections';
import { savePracticeResults, updateUserRevisionStats } from '../../utils/revisionUtils';
import { calculateRealTestResults, calculateTestStatistics, calculateRankAndPercentile } from '../../utils/realTestResultsCalculator';
import EnhancedTestInterface from '../../components/EnhancedTestInterface';
import CATTestInterface from '../../components/CATTestInterface';
import CATInstructions from '../../components/CATInstructions';
import TestSummaryTable from '../../components/TestSummaryTable';
import CameraMonitor from '../../components/proctoring/CameraMonitor';

const TestInterface = () => {
  const { testId: rawTestId, examId } = useParams();
  // React Router automatically decodes URL parameters
  // So if URL has "ssc%20cgl", useParams() returns "ssc cgl"
  // Use it as-is since Firebase document IDs can contain spaces
  const testId = rawTestId;
  const navigate = useNavigate();
  const location = useLocation();
  const { updateUserPerformance, userPerformance, userStats } = useUserData();
  const { currentTest, setCurrentTest } = useTest();
  const { currentUser, userProfile } = useRealtime();
  
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(0);
  const [isStarted, setIsStarted] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [showResults, setShowResults] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isReviewMode, setIsReviewMode] = useState(false);
  const [selectedAnswers, setSelectedAnswers] = useState(null);
  const [testInfo, setTestInfo] = useState(null);
  const [testData, setTestData] = useState(null); // Store full test data for manual evaluation
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [testStartTime, setTestStartTime] = useState(null);
  const [isManualEvaluation, setIsManualEvaluation] = useState(false); // Track if manual evaluation mode
  const [questionTimes, setQuestionTimes] = useState({});
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(true);
  const [showMaliciousActivityWarning, setShowMaliciousActivityWarning] = useState(false);
  const [showUserDetailsModal, setShowUserDetailsModal] = useState(false);
  const [userDetails, setUserDetails] = useState({ name: '', email: '' });
  const [savingUserDetails, setSavingUserDetails] = useState(false);
  const [maliciousActivityDetected, setMaliciousActivityDetected] = useState(false);
  const [objectDetectionViolations, setObjectDetectionViolations] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const startTimeRef = useRef(null);
  const questionStartTimeRef = useRef(null);
  const questionTimesRef = useRef({}); // Store accumulated times
  const timerIntervalRef = useRef(null);
  const handleSubmitTestRef = useRef(null);
  const hasAutoSubmittedRef = useRef(false);
  const tabSwitchCountRef = useRef(0);
  const visibilityChangeHandlerRef = useRef(null);
  const blurHandlerRef = useRef(null);
  const beforeUnloadHandlerRef = useRef(null);

  const loadTestData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      // Handle case when no testId is provided (direct access)
      const actualTestId = testId || 'test-1';
      const actualExamId = examId || 'cat-quantitative';
      
      // Check if this is a revision test or practice test
      const isRevisionTest = actualTestId.startsWith('revision-');
      const isPracticeTest = actualTestId.startsWith('practice-');
      
              // Load test information from Firebase (skip for revision and practice tests)
        if (!isRevisionTest && !isPracticeTest) {
          const testInfoResult = await getMockTest(actualTestId);
          
          if (testInfoResult.success && testInfoResult.data) {
            const testInfo = testInfoResult.data;
          
          // Update test info with correct question count if using embedded questions
          if (testInfo.originalTestData && testInfo.originalTestData.questions) {
            testInfo.totalQuestions = testInfo.originalTestData.questions.length;
          }
          
              // Check for sections in originalTestData if not in testInfo
          const sections = testInfo.sections || testInfo.originalTestData?.sections || [];
          const useDynamicSections = testInfo.useDynamicSections !== undefined 
            ? testInfo.useDynamicSections 
            : (testInfo.originalTestData?.useDynamicSections || false);
          
          // Load full test data for interface type and other settings
          const fullTestDataResult = await getTestById(actualTestId);
          let fullTestData = null;
          if (fullTestDataResult.success && fullTestDataResult.data) {
            fullTestData = fullTestDataResult.data;
            setTestData(fullTestData);
          }
          
          setTestInfo({
            ...testInfo,
            examId: actualExamId,
            // Ensure sections are included
            sections: sections,
            useDynamicSections: useDynamicSections,
            // Include marking scheme from test or originalTestData
            markingScheme: testInfo.markingScheme || testInfo.originalTestData?.markingScheme || { correct: 4, incorrect: -1, partial: 0 },
            // Include feature flags from originalTestData if available
            enableLanguageSwitch: testInfo.enableLanguageSwitch !== undefined 
              ? testInfo.enableLanguageSwitch 
              : (testInfo.originalTestData?.enableLanguageSwitch || false),
            enableCalculator: testInfo.enableCalculator !== undefined 
              ? testInfo.enableCalculator 
              : (testInfo.originalTestData?.enableCalculator || false),
            enableQuestionPaper: testInfo.enableQuestionPaper !== undefined 
              ? testInfo.enableQuestionPaper 
              : (testInfo.originalTestData?.enableQuestionPaper || false),
            // Include interface type from fullTestData or originalTestData
            interfaceType: fullTestData?.interfaceType || testInfo.interfaceType || testInfo.originalTestData?.interfaceType || 'jee',
            // Include passages and sets for CAT tests
            passages: testInfo.originalTestData?.passages || [],
            sets: testInfo.originalTestData?.sets || []
          });
          setTimeLeft(testInfo.duration || 60 * 60);
                  } else {
            // If no test found, show error with more details
            setError(`Test not found. ${testInfoResult.error || 'Please select a valid test.'}`);
            return;
          }
      }

      // Load questions
      let examQuestions = [];
      
      // Handle revision tests
      if (isRevisionTest && currentUser?.uid) {
        try {
          const wrongQuestionsResult = await getUserWrongQuestions(currentUser.uid, 50);
          if (wrongQuestionsResult.success && wrongQuestionsResult.data.length > 0) {
            examQuestions = wrongQuestionsResult.data.map((wrongQ, index) => ({
              id: `revision-${wrongQ.questionId || index}`,
              question: wrongQ.question,
              type: wrongQ.type || 'multiple-choice',
              options: wrongQ.options || [],
              correctAnswer: wrongQ.correctAnswer,
              explanation: wrongQ.explanation || `The correct answer is ${wrongQ.options ? wrongQ.options[wrongQ.correctAnswer] : wrongQ.correctAnswer}`,
              topic: wrongQ.topic,
              difficulty: wrongQ.difficulty || 'medium',
              subject: wrongQ.subject || wrongQ.topic,
              allowMultiple: false,
              markingScheme: wrongQ.markingScheme || { correct: 4, incorrect: -1, unattempted: 0 }
            }));
            
            examQuestions = examQuestions.sort(() => Math.random() - 0.5).slice(0, Math.min(20, examQuestions.length));
            
            setTestInfo(prev => ({
              ...prev,
              name: 'Revision Test - Wrong Questions',
              totalQuestions: examQuestions.length,
              duration: examQuestions.length * 3 * 60, // 3 minutes per question
              isRevision: true,
              examId: actualExamId,
              markingScheme: { correct: 4, incorrect: -1, partial: 0 }
            }));
            setTimeLeft(examQuestions.length * 3 * 60);
          } else {
            setError('No wrong questions found for revision. Take some tests first to generate wrong questions.');
            return;
          }
        } catch (error) {
          setError('Failed to load wrong questions for revision. Please try again.');
          return;
        }
      }
      
      // Handle practice tests
      if (isPracticeTest && currentUser?.uid) {
        try {
          // Get practice test data from localStorage (set by revision page)
          const practiceTestData = localStorage.getItem(`practice-test-${actualTestId}`);
          if (practiceTestData) {
            const practiceData = JSON.parse(practiceTestData);
            examQuestions = practiceData.questions.map((q, index) => ({
              id: q.id || `practice-${index}`,
              question: q.question,
              type: q.type || 'multiple-choice',
              options: q.options || [],
              correctAnswer: q.correctAnswer,
              explanation: q.explanation || '',
              topic: q.topic || '',
              difficulty: q.difficulty || 'medium',
              subject: q.subject || q.topic || 'General',
              allowMultiple: q.allowMultiple || false,
              markingScheme: q.markingScheme || { correct: 4, incorrect: -1, unattempted: 0 }
            }));
            
            setTestInfo(prev => ({
              ...prev,
              name: practiceData.name || 'Practice Test',
              totalQuestions: examQuestions.length,
              duration: practiceData.duration || examQuestions.length * 2 * 60, // 2 minutes per question
              isPractice: true,
              topic: practiceData.topic,
              examId: actualExamId,
              markingScheme: practiceData.markingScheme || { correct: 4, incorrect: -1, partial: 0 }
            }));
            setTimeLeft(practiceData.duration || examQuestions.length * 2 * 60);
          } else {
            setError('Practice test data not found. Please try starting the practice session again.');
            return;
          }
        } catch (error) {
          setError('Failed to load practice test data. Please try again.');
          return;
        }
      }
      
      // Load questions from Firebase
      if (examQuestions.length === 0) {
        // Loading questions from Firebase
        
        // Check if the test has embedded questions (from admin panel)
        if (testInfo && testInfo.originalTestData && testInfo.originalTestData.questions && testInfo.originalTestData.questions.length > 0) {
          examQuestions = testInfo.originalTestData.questions.map((q, index) => ({
            id: q.id || `q-${index}`,
            question: q.question,
            type: q.type || 'multiple-choice',
            options: q.options || [],
            correctAnswer: q.correctAnswer,
            explanation: q.explanation || '',
            topic: q.topic || '',
            difficulty: q.difficulty || 'medium',
            subject: q.subject || q.topic || 'General',
            allowMultiple: q.allowMultiple || false,
            // Include RC/LRDI specific fields
            passage: q.passage || '',
            setContent: q.setContent || '',
            passageGroupId: q.passageGroupId || '',
            setGroupId: q.setGroupId || '',
            questionNumberInGroup: q.questionNumberInGroup || 1,
            markingScheme: q.markingScheme || { correct: 4, incorrect: -1, unattempted: 0 }
          }));
          
          // Update test info with the actual number of questions loaded
          setTestInfo(prev => ({
            ...prev,
            totalQuestions: examQuestions.length
          }));
        } else {
          // Load questions from the test document itself
          const result = await getQuestionsFromTest(actualTestId);
          examQuestions = result.success ? result.data : [];
          
          // Update test info with the actual number of questions loaded
          if (result.success) {
            setTestInfo(prev => ({
              ...prev,
              totalQuestions: examQuestions.length
            }));
          }
          
          // If no questions found in test, fallback to question bank
          if (examQuestions.length === 0) {
            const bankResult = await getQuestionsFromBank({
              limit: testInfo?.totalQuestions || 20,
              exam: actualExamId
            });
            examQuestions = bankResult.success ? bankResult.data : [];
            
            if (bankResult.success) {
              setTestInfo(prev => ({
                ...prev,
                totalQuestions: examQuestions.length
              }));
            }
          }
        }
      }

      // Add default marking scheme if not present
      examQuestions = examQuestions.map(q => ({
        ...q,
        markingScheme: q.markingScheme || { correct: 4, incorrect: -1, unattempted: 0 }
      }));

      // Use all loaded questions since we've already updated testInfo.totalQuestions
      if (examQuestions.length === 0) {
        setError('No questions found for this test. Please try another test.');
        return;
      }
      
      setQuestions(examQuestions);
      
      // Load saved progress if exists (don't let errors here break the test loading)
      try {
        await loadProgress();
      } catch (progressError) {
        // Continue even if progress loading fails
      }
      
    } catch (error) {
      setError(`Failed to load test data: ${error.message || 'Unknown error'}. Please try again.`);
    } finally {
      setIsLoading(false);
    }
  }, [testId, examId, currentUser?.uid]);

  const handleSubmitTest = useCallback(async () => {
    if (!currentUser?.uid) {
      setError('User not authenticated. Please login to submit test.');
      return;
    }

    if (isSubmitting) {
      return;
    }

    // Clear timer when submitting
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }


    // Exit fullscreen when submitting
    try {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else if (document.webkitFullscreenElement) {
        document.webkitExitFullscreen();
      } else if (document.msFullscreenElement) {
        document.msExitFullscreen();
      }
    } catch (error) {
      // Ignore errors when exiting fullscreen
    }

    try {
      setIsSubmitting(true);
      
      // Get test data for real calculation
      const testDataResult = await getTestById(testId);
      const testData = testDataResult.success ? testDataResult.data : {
        questions: questions,
        duration: (testInfo?.duration || 60) * 60, // Convert minutes to seconds
        title: testInfo?.name || 'Test',
        exam: testInfo?.examId || 'general',
        markingScheme: { correct: 4, incorrect: -1, unattempted: 0 }
      };
      
      // Normalize duration to seconds if it's in minutes
      if (testData.duration && testData.duration < 1000) {
        // If duration is less than 1000, it's likely in minutes, convert to seconds
        testData.duration = testData.duration * 60;
      }
      
      // Extract analysis video URL from test data
      const analysisVideoUrl = testData?.analysisVideoUrl || null;

      // Check if manual evaluation mode is enabled
      const isManualEvaluation = testData?.manualEvaluation || false;

      // Ensure questions are properly set
      if (!testData.questions && questions.length > 0) {
        testData.questions = questions;
      }

      // Ensure test configuration is properly set for sectional analysis
      if (testData && !testData.testConfig) {
        testData.testConfig = {
          useDynamicSections: testData.useDynamicSections || false,
          sections: testData.sections || []
        };
      }

      // Ensure sections are properly configured for tests with useDynamicSections
      if (testData.useDynamicSections && testData.sections && testData.sections.length > 0) {
        if (!testData.testConfig) {
          testData.testConfig = {
            useDynamicSections: true,
            sections: testData.sections
          };
        }
      }

      // Prepare user answers data with proper validation
      const cleanAnswers = {};
      Object.keys(answers).forEach(key => {
        const answer = answers[key];
        // Only include valid answers (not null, undefined, empty string, or string 'null'/'undefined')
        if (answer !== null && answer !== undefined && answer !== '' && answer !== 'null' && answer !== 'undefined') {
          cleanAnswers[key] = answer;
        }
      });

      const userAnswers = {
        startTime: testStartTime || Date.now(),
        endTime: Date.now(),
        answers: cleanAnswers,
        timeSpent: questionTimesRef.current // Use ref instead of state for most up-to-date values
      };

        // Apply score penalty if malicious activity was detected
        if (maliciousActivityDetected) {
          // Set score to -5 as penalty
          userAnswers.maliciousActivityDetected = true;
          userAnswers.penaltyApplied = true;
        }

        // For manual evaluation mode, skip auto-calculation and save answers with questions
      if (isManualEvaluation) {
        // Get user name and email from userProfile or currentUser
        const userName = userProfile?.name || userProfile?.displayName || currentUser?.displayName || currentUser?.email?.split('@')[0] || 'Unknown';
        const userEmail = userProfile?.email || currentUser?.email || '';
        
        // Save test result with questions and answers for manual evaluation
        const manualTestResult = {
          testId: testId,
          userId: currentUser.uid,
          userName: userName,
          userEmail: userEmail,
          testName: testData.title || testInfo?.name || 'Test',
          exam: testData.exam || testInfo?.examId || 'general',
          testType: 'Manual Evaluation',
          manualEvaluation: true,
          status: 'pending_evaluation', // Status to indicate pending admin evaluation
          startTime: new Date(testStartTime || Date.now()).toISOString(),
          endTime: new Date().toISOString(),
          completedAt: new Date().toISOString(),
          totalTimeSpent: Math.floor((Date.now() - (testStartTime || Date.now())) / 1000),
          // Save questions with user answers
          questionsWithAnswers: testData.questions.map((question, index) => {
            const questionId = question.id || `q${index}`;
            return {
              ...question,
              userAnswer: cleanAnswers[questionId] || null,
              questionNumber: index + 1
            };
          }),
          answers: cleanAnswers,
          timeSpent: questionTimesRef.current, // Use ref instead of state for most up-to-date values
          // Calculate attempted/unattempted for summary
          attempted: Object.keys(cleanAnswers).length,
          unattempted: (testData.questions?.length || questions.length) - Object.keys(cleanAnswers).length,
          totalQuestions: testData.questions?.length || questions.length
        };

        // Save manual evaluation result
        const saveResult = await saveTestResult(manualTestResult);
        if (!saveResult.success) {
          throw new Error(`Failed to save test result: ${saveResult.error}`);
        }

        // Store test data and set manual evaluation flag
        setTestData(testData);
        setIsManualEvaluation(true);
        
        // Set state to show summary instead of results
        setIsCompleted(true);
        setShowResults(true);
        setIsSubmitting(false);
        return; // Exit early for manual evaluation
      }

        // Calculate real test results (only for auto-evaluation mode)
      const realResults = await calculateRealTestResults(userAnswers, testData, currentUser);
      
      // Apply -5 score penalty if malicious activity detected
      if (maliciousActivityDetected) {
        realResults.score = -5;
        realResults.percentageScore = -5;
        realResults.penaltyApplied = true;
        realResults.maliciousActivityDetected = true;
      }
      
      // Get all existing results for this test to calculate rank and statistics
      const allResultsResult = await getTestResultsByTestId(testId);
      const allResults = allResultsResult.success ? allResultsResult.data : [];
      
      // Calculate rank and percentile - include current user's score in the comparison
      const allScores = allResults.map(r => r.score);
      // Add current user's score to the array for proper ranking calculation
      allScores.push(realResults.score);
      const { rank, totalParticipants, percentile } = calculateRankAndPercentile(realResults.score, allScores);
      
      // Add rank and percentile to results
      const testResult = {
        ...realResults,
        rank,
        totalParticipants,
        percentile,
        // Copy analysis video URL from test if available
        ...(analysisVideoUrl && { analysisVideoUrl })
      };

      // Filter out undefined values and validate data to prevent Firebase errors
      const cleanTestResult = Object.fromEntries(
        Object.entries(testResult).filter(([key, value]) => {
          // Filter out undefined values
          if (value === undefined) return false;
          
          // Ensure numeric fields are numbers
          if (['score', 'totalMarks', 'percentageScore', 'correctAnswers', 'wrongAnswers', 'unattempted', 'attempted', 'accuracy', 'totalTimeSpent', 'timeUtilization'].includes(key)) {
            return typeof value === 'number' && !isNaN(value);
          }
          
          // Ensure required fields are present
          if (['testId', 'userId', 'testName'].includes(key)) {
            return value !== null && value !== '';
          }
          
          return true;
        })
      );

      // Add testName if it's missing
      if (!cleanTestResult.testName && testInfo?.name) {
        cleanTestResult.testName = testInfo.name;
      }

      // Ensure required fields are present
      if (!cleanTestResult.testId) {
        cleanTestResult.testId = testId;
      }
      if (!cleanTestResult.userId) {
        cleanTestResult.userId = currentUser.uid;
      }
      if (!cleanTestResult.testName) {
        cleanTestResult.testName = testInfo?.name || 'Test';
      }

      // Convert any Date objects to ISO strings
      if (cleanTestResult.startTime instanceof Date) {
        cleanTestResult.startTime = cleanTestResult.startTime.toISOString();
      }
      if (cleanTestResult.endTime instanceof Date) {
        cleanTestResult.endTime = cleanTestResult.endTime.toISOString();
      }
      if (cleanTestResult.completedAt instanceof Date) {
        cleanTestResult.completedAt = cleanTestResult.completedAt.toISOString();
      }

      // Store test result with comprehensive analytics
      
      // First try the enhanced storage
      let storeResult = await storeTestResultWithAnalytics(cleanTestResult);
      
      if (!storeResult.success) {
        // Fallback to basic storage
        const basicResult = await saveTestResult(cleanTestResult);
        if (!basicResult.success) {
          throw new Error(`Failed to save test result: ${basicResult.error}`);
        }
        
        storeResult = {
          success: true,
          resultId: basicResult.id,
          analytics: null
        };
      }



      // Update performance data
      await updateUserPerformance({
        totalTestsTaken: (userStats?.totalTestsTaken || 0) + 1,
        averageScore: calculateNewAverage(userPerformance?.averageScore || 0, testResult.percentageScore, userStats?.totalTestsTaken || 0),
        lastTestDate: new Date().toISOString(),
        totalScore: (userPerformance?.totalScore || 0) + testResult.score,
        totalCorrectAnswers: (userPerformance?.totalCorrectAnswers || 0) + testResult.correctAnswers,
        totalQuestions: (userPerformance?.totalQuestions || 0) + questions.length
      });

      // Save wrong and skipped questions for revision
      try {

        // Save both wrong answers AND skipped questions
        const revisionQuestions = testResult.detailedResults
          .filter(result => {
            // Include wrong answers (isCorrect === false)
            const isWrong = result.isCorrect === false;
            // Include skipped questions (userAnswer is null, undefined, or empty)
            const isSkipped = result.userAnswer === null || result.userAnswer === undefined || result.userAnswer === '' || result.userAnswer === 'null' || result.userAnswer === 'undefined';
            return isWrong || isSkipped;
          })
          .map(result => {
            const question = questions.find(q => q.id === result.questionId);
            const isSkipped = result.userAnswer === null || result.userAnswer === undefined || result.userAnswer === '' || result.userAnswer === 'null' || result.userAnswer === 'undefined';
            const isWrong = result.isCorrect === false && !isSkipped;
            
            const revisionQ = {
              userId: currentUser.uid,
              questionId: result.questionId,
              question: question?.question || '',
              questionType: question?.type || 'multiple-choice', // Store the actual question type
              type: question?.type || 'multiple-choice', // Ensure type is set
              options: question?.options || ['Option A', 'Option B', 'Option C', 'Option D'], // Provide default options
              userAnswer: isSkipped ? null : result.userAnswer, // Ensure skipped questions have null userAnswer
              correctAnswer: question?.correctAnswer || 'Answer not available', // Provide fallback
              explanation: question?.explanation || '',
              topic: question?.topic || question?.subject || 'General',
              subject: question?.subject || question?.topic || 'General',
              course: question?.course || testInfo?.course || 'General Course',
              chapter: question?.chapter || question?.section || 'General Chapter',
              difficulty: question?.difficulty || 'medium',
              testId: testId,
              testName: testInfo?.name || 'Test',
              timeSpent: questionTimes[result.questionId] || 0,
              status: isSkipped ? 'skipped' : 'wrong', // Properly set status
              isSkipped: isSkipped,
              isWrong: isWrong,
              createdAt: new Date().toISOString()
            };

            return revisionQ;
          });


        // Save each revision question (both wrong and skipped)
        console.log(`Saving ${revisionQuestions.length} revision questions (wrong + skipped)`);
        let savedCount = 0;
        let failedCount = 0;
        
        for (const revisionQ of revisionQuestions) {
          const saveResult = await saveWrongQuestion(currentUser.uid, revisionQ);
          if (saveResult.success) {
            savedCount++;
          } else {
            failedCount++;
            console.error('Failed to save revision question:', revisionQ.questionId, saveResult.error);
          }
        }
        
        console.log(`Revision questions saved: ${savedCount} success, ${failedCount} failed`);
      } catch (error) {
        console.error('Error saving revision questions:', error);
        // Don't fail the entire test submission for this error
      }

      // Handle revision and practice test results
      if (testInfo?.isRevision || testInfo?.isPractice) {
        try {
          if (testInfo.isPractice) {
            // Save practice session results
            await savePracticeResults(currentUser.uid, {
              topic: testInfo.topic || 'General',
              questions: questions,
              correctAnswers: testResult.correctAnswers,
              totalQuestions: questions.length,
              duration: testResult.totalTimeSpent,
              accuracy: testResult.accuracy,
              mode: testInfo.mode || 'timed'
            });
          } else if (testInfo.isRevision) {
            // Update revision statistics
            await updateUserRevisionStats(currentUser.uid, {
              topic: testInfo.topic || 'General',
              accuracy: testResult.accuracy,
              questionsAnswered: questions.length,
              correctAnswers: testResult.correctAnswers
            });
          }
        } catch (error) {
          // Don't fail the entire test submission for this error
        }
      }

      // Clear all test cache
      clearTestCache();

      // Always dispatch revision data refresh event (revision page will listen for it)
      console.log('Test submitted: Dispatching refreshRevisionData event');
      window.dispatchEvent(new CustomEvent('refreshRevisionData'));

      // Format data for TestResults component using real results
      const resultsData = {
        ...testResult,
        testId: testId,
        testName: testInfo?.name || 'Test',
        answers: cleanAnswers, // Use cleanAnswers instead of raw answers
        questions: questions.map((q, idx) => ({
          id: q.id,
          question: q.question,
          options: q.options || [],
          correctAnswer: q.correctAnswer,
          userAnswer: cleanAnswers[q.id] || null, // Use cleanAnswers and ensure null for unattempted
          explanation: q.explanation || `The correct answer is ${q.options ? q.options[q.correctAnswer] : q.correctAnswer}`,
          topic: q.topic,
          difficulty: q.difficulty,
          type: q.type,
          isCorrect: testResult.detailedResults?.[idx]?.isCorrect || false,
          timeSpent: questionTimes[q.id] || 0
        })),
        // Ensure sectional results are included
        sectionalResults: testResult.sectionalResults || [],
        testConfig: testData.testConfig || {
          useDynamicSections: testData.useDynamicSections || false,
          sections: testData.sections || []
        }
      };



      // Navigate to results page - encode testId to handle spaces
      const encodedTestId = encodeURIComponent(testId);
      navigate(`/dashboard/test-results/${encodedTestId}`, { 
        state: resultsData
      });

    } catch (error) {
      // Test submission error
      setError(`Failed to submit test: ${error.message}. Please try again.`);
    } finally {
      setIsSubmitting(false);
    }
  }, [currentUser?.uid, isSubmitting, testId, testInfo, questions, answers, questionTimes, testStartTime, userStats, userPerformance, navigate]);

  // Helper function to request fullscreen with proper error handling
  const requestFullscreen = useCallback(async () => {
    try {
      const element = document.documentElement;
      
      // Check if fullscreen is already active
      if (document.fullscreenElement || document.webkitFullscreenElement || document.msFullscreenElement) {
        return true;
      }

      // Try standard fullscreen API
      if (element.requestFullscreen) {
        await element.requestFullscreen();
        return true;
      }
      // Try WebKit fullscreen (Safari)
      else if (element.webkitRequestFullscreen) {
        await element.webkitRequestFullscreen();
        return true;
      }
      // Try MS fullscreen (IE/Edge)
      else if (element.msRequestFullscreen) {
        await element.msRequestFullscreen();
        return true;
      }
      // Try Mozilla fullscreen
      else if (element.mozRequestFullScreen) {
        await element.mozRequestFullScreen();
        return true;
      }
      
      return false;
    } catch (error) {
      // Handle specific error types - test can continue without fullscreen
      return false;
    }
  }, []);


  // Keep handleSubmitTest ref updated
  useEffect(() => {
    handleSubmitTestRef.current = handleSubmitTest;
  }, [handleSubmitTest]);

  // Fullscreen and tab switching prevention when test is started
  useEffect(() => {
    if (!isStarted || isSubmitting) {
      return;
    }

    // Prevent tab switching and window minimizing
    const handleVisibilityChange = () => {
      if (document.hidden) {
        tabSwitchCountRef.current += 1;
        // Warn user and potentially submit test after multiple violations
        if (tabSwitchCountRef.current >= 3) {
          // Mark malicious activity detected
          setMaliciousActivityDetected(true);
          setShowMaliciousActivityWarning(true);
          if (tabSwitchCountRef.current >= 5) {
            // Auto-submit after 5 violations with penalty
            setMaliciousActivityDetected(true);
            if (handleSubmitTestRef.current) {
              handleSubmitTestRef.current();
            }
            return;
          }
        } else {
          setShowMaliciousActivityWarning(true);
        }
        // Try to refocus the window
        window.focus();
      }
    };

    const handleBlur = () => {
      // Detect when window loses focus
      if (document.hidden === false) {
        // Window is still visible but lost focus - might be alt-tab
        tabSwitchCountRef.current += 1;
        if (tabSwitchCountRef.current >= 3) {
          setShowMaliciousActivityWarning(true);
        }
      }
    };

    const handleBeforeUnload = (e) => {
      // Prevent closing the window/tab
      e.preventDefault();
      e.returnValue = 'Are you sure you want to leave? Your test progress will be lost.';
      return e.returnValue;
    };

    // Prevent context menu (right-click)
    const handleContextMenu = (e) => {
      e.preventDefault();
      return false;
    };

    // Prevent exiting fullscreen during test
    const handleFullscreenChange = () => {
      const isFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.msFullscreenElement
      );
      
      if (!isFullscreen && isStarted && !isSubmitting) {
        // User tried to exit fullscreen OR camera permission caused exit - re-enter it
        setTimeout(async () => {
          await requestFullscreen();
        }, 200);
      }
    };

    // Prevent common keyboard shortcuts
    const handleKeyDown = (e) => {
      // Prevent F11 (fullscreen toggle)
      if (e.key === 'F11') {
        e.preventDefault();
        setShowMaliciousActivityWarning(true);
        return false;
      }
      // Prevent Escape key (often used to exit fullscreen)
      if (e.key === 'Escape') {
        // Only prevent if we're in fullscreen
        const isFullscreen = !!(
          document.fullscreenElement ||
          document.webkitFullscreenElement ||
          document.msFullscreenElement
        );
        if (isFullscreen) {
          e.preventDefault();
          setShowMaliciousActivityWarning(true);
          return false;
        }
      }
      // Prevent Alt+Tab (though this is OS-level and hard to prevent completely)
      if (e.altKey && e.key === 'Tab') {
        e.preventDefault();
        setShowMaliciousActivityWarning(true);
        return false;
      }
      // Prevent Ctrl+W or Cmd+W (close tab)
      if ((e.ctrlKey || e.metaKey) && e.key === 'w') {
        e.preventDefault();
        setShowMaliciousActivityWarning(true);
        return false;
      }
      // Prevent Ctrl+T or Cmd+T (new tab)
      if ((e.ctrlKey || e.metaKey) && e.key === 't') {
        e.preventDefault();
        setShowMaliciousActivityWarning(true);
        return false;
      }
      // Prevent Ctrl+Shift+T or Cmd+Shift+T (reopen closed tab)
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'T') {
        e.preventDefault();
        return false;
      }
      // Prevent Ctrl+N or Cmd+N (new window)
      if ((e.ctrlKey || e.metaKey) && e.key === 'n') {
        e.preventDefault();
        return false;
      }
    };

    // Store handlers in refs for cleanup
    visibilityChangeHandlerRef.current = handleVisibilityChange;
    blurHandlerRef.current = handleBlur;
    beforeUnloadHandlerRef.current = handleBeforeUnload;

    // Add event listeners
    document.addEventListener('visibilitychange', handleVisibilityChange);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('msfullscreenchange', handleFullscreenChange);
    window.addEventListener('blur', handleBlur);
    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup function
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('msfullscreenchange', handleFullscreenChange);
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeyDown);
      
      // Exit fullscreen when test ends
      try {
        if (document.fullscreenElement) {
          document.exitFullscreen();
        } else if (document.webkitFullscreenElement) {
          document.webkitExitFullscreen();
        } else if (document.msFullscreenElement) {
          document.msExitFullscreen();
        }
      } catch (error) {
        // Ignore errors when exiting fullscreen
      }
    };
  }, [isStarted, isSubmitting, requestFullscreen]);


  const saveProgress = useCallback(async () => {
    try {
      const progress = {
        answers,
        timeLeft,
        currentQuestionIndex,
        questionTimes,
        timestamp: new Date().toISOString()
      };
      localStorage.setItem(`test-progress-${testId}`, JSON.stringify(progress));
    } catch (error) {
      }
  }, [answers, timeLeft, currentQuestionIndex, questionTimes, testId]);

  // Load test information and questions
  useEffect(() => {
    loadTestData();
  }, [loadTestData]);

  // Timer effect with improved functionality - runs continuously without restarting
  useEffect(() => {
    // Clear any existing timer
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }

    // Only start timer if test is started, not loading, and not submitting
    if (isStarted && !isLoading && !isSubmitting) {
      timerIntervalRef.current = setInterval(() => {
        setTimeLeft(prev => {
          // If time is up or about to be up, submit the test
          if (prev <= 1 && !hasAutoSubmittedRef.current) {
            // Mark as auto-submitted to prevent multiple submissions
            hasAutoSubmittedRef.current = true;
            // Clear the timer
            if (timerIntervalRef.current) {
              clearInterval(timerIntervalRef.current);
              timerIntervalRef.current = null;
            }
            // Submit the test using the ref to avoid dependency issues
            if (handleSubmitTestRef.current) {
              handleSubmitTestRef.current();
            }
            return 0;
          }
          // Decrement time
          return prev - 1;
        });
      }, 1000);
    }

    // Cleanup function
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, [isStarted, isLoading, isSubmitting]); // Only depend on these, not timeLeft

  // Separate effect to handle edge case where time is already 0 when test starts
  useEffect(() => {
    if (isStarted && !isLoading && !isSubmitting && timeLeft <= 0 && !hasAutoSubmittedRef.current) {
      // Time is already up, submit immediately (only once)
      hasAutoSubmittedRef.current = true;
      if (handleSubmitTestRef.current) {
        handleSubmitTestRef.current();
      }
    }
  }, [isStarted, isLoading, isSubmitting, timeLeft]);

  // Auto-save functionality
  useEffect(() => {
    if (autoSaveEnabled && Object.keys(answers).length > 0) {
      const autoSaveTimer = setTimeout(() => {
        saveProgress();
      }, 30000); // Auto-save every 30 seconds

      return () => clearTimeout(autoSaveTimer);
    }
  }, [answers, autoSaveEnabled, saveProgress]);

  // Track question time with refs for accuracy
  useEffect(() => {
    if (!isStarted || !questions[currentQuestionIndex]) {
      return;
    }

    const currentQuestionId = questions[currentQuestionIndex].id;
    
    // Get the base time (previously accumulated time for this question)
    const baseTime = questionTimesRef.current[currentQuestionId] || 0;
    
    // Record start time for this question
    const startTime = Date.now();
    
    // Update display every second
    const questionTimer = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      const currentTime = baseTime + elapsed;
      
      // Update only this question's time in the ref
      questionTimesRef.current[currentQuestionId] = currentTime;
      
      // Update state with all question times from ref
      setQuestionTimes({ ...questionTimesRef.current });
    }, 1000);

    // Cleanup: save final time when leaving question
    return () => {
      clearInterval(questionTimer);
      
      const finalElapsed = Math.floor((Date.now() - startTime) / 1000);
      const finalTime = baseTime + finalElapsed;
      
      // Store in ref for persistence
      questionTimesRef.current[currentQuestionId] = finalTime;
      
      // Update state with all question times from ref
      setQuestionTimes({ ...questionTimesRef.current });
    };
  }, [isStarted, currentQuestionIndex]);


  const loadProgress = async () => {
    try {
      if (!testId) return;
      const savedProgress = localStorage.getItem(`test-progress-${testId}`);
      if (savedProgress) {
        const progress = JSON.parse(savedProgress);
        setAnswers(progress.answers || {});
        setTimeLeft(progress.timeLeft || testInfo?.duration || 3600);
        setCurrentQuestionIndex(progress.currentQuestionIndex || 0);
        const savedTimes = progress.questionTimes || {};
        setQuestionTimes(savedTimes);
        questionTimesRef.current = { ...savedTimes }; // Sync ref with saved times
      }
    } catch (error) {
      // Silently fail - progress loading is not critical
    }
  };


  // Clear all test-related cache when test is completed
  const clearTestCache = () => {
    try {
      // Clear progress for this test
      localStorage.removeItem(`test-progress-${testId}`);
      
      // Clear any cached questions for this test
      localStorage.removeItem(`test-questions-${testId}`);
      
      // Clear any cached test info
      localStorage.removeItem(`test-info-${testId}`);
      
      // Clear practice test data if it exists
      localStorage.removeItem(`practice-test-${testId}`);
      
      } catch (error) {
      }
  };



  const startTest = async () => {
    // Check interface type
    const interfaceType = testData?.interfaceType || testInfo?.interfaceType || 'jee';
    
    // For CAT interface, skip proctoring setup and malicious activity warning, go directly to test
    if (interfaceType === 'cat') {
      try {
        // Clear any existing cache for this test to ensure fresh questions
        clearTestCache();
        
        // Reset question times for fresh test
        setQuestionTimes({});
        questionTimesRef.current = {};
        
        // Reset auto-submit flag when starting a new test
        hasAutoSubmittedRef.current = false;
        tabSwitchCountRef.current = 0;
        setMaliciousActivityDetected(false);
        
        // Start the test directly
        setIsStarted(true);
        setTestStartTime(new Date());
        startTimeRef.current = Date.now();
        // Generate session ID for watermarking
        setSessionId(`session_${testId}_${currentUser.uid}_${Date.now()}`);
      } catch (error) {
        setError('Failed to start test. Please try again.');
      }
      return;
    }

    // For JEE interface, follow normal flow

    // Check if this is a manual evaluation test and if user details are missing
    const isManualEval = testInfo?.manualEvaluation || testInfo?.originalTestData?.manualEvaluation;
    if (isManualEval) {
      // Get user name and email from userProfile or currentUser
      const userName = userProfile?.name || userProfile?.displayName || currentUser?.displayName || '';
      const userEmail = userProfile?.email || currentUser?.email || '';
      
      // If either is missing, show modal to collect details
      if (!userName || !userEmail) {
        setUserDetails({
          name: userName || '',
          email: userEmail || ''
        });
        setShowUserDetailsModal(true);
        return;
      }
    }

    // Show malicious activity warning first
    setShowMaliciousActivityWarning(true);
  };

  const handleWarningAcknowledged = async () => {
    try {
      setShowMaliciousActivityWarning(false);
      
      // Clear any existing cache for this test to ensure fresh questions
      clearTestCache();
      
      // Reset question times for fresh test
      setQuestionTimes({});
      questionTimesRef.current = {};
      
      // Reset auto-submit flag when starting a new test
      hasAutoSubmittedRef.current = false;
      tabSwitchCountRef.current = 0;
      setMaliciousActivityDetected(false);
      
      // Enter fullscreen mode (non-blocking - test continues even if it fails)
      try {
        await requestFullscreen();
      } catch (fullscreenError) {
        // Continue with test even if fullscreen fails
      }
      
      // Start the test directly (proctoring via camera monitoring is already explained in warning)
      setIsStarted(true);
      setTestStartTime(new Date());
      startTimeRef.current = Date.now();
      // Generate session ID for watermarking
      setSessionId(`session_${testId}_${currentUser.uid}_${Date.now()}`);
      
      // Ensure fullscreen is maintained after test starts
      setTimeout(async () => {
        const isFullscreen = !!(
          document.fullscreenElement ||
          document.webkitFullscreenElement ||
          document.msFullscreenElement
        );
        if (!isFullscreen) {
          try {
            await requestFullscreen();
          } catch (e) {
            // Ignore fullscreen errors
          }
        }
      }, 1000);
    } catch (error) {
      setError('Failed to start test. Please try again.');
      setShowMaliciousActivityWarning(false);
    }
  };


  // Proctoring setup is no longer used - handled directly in handleWarningAcknowledged

  const handleUserDetailsSubmit = async () => {
    if (!userDetails.name.trim() || !userDetails.email.trim()) {
      setError('Please enter both name and email');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(userDetails.email)) {
      setError('Please enter a valid email address');
      return;
    }

    setSavingUserDetails(true);
    setError(null);

    try {
      // Update user profile in Firebase
      const updateResult = await updateUserProfile(currentUser.uid, {
        name: userDetails.name.trim(),
        displayName: userDetails.name.trim(),
        email: userDetails.email.trim()
      });

      if (updateResult.success) {
        // Close modal and proceed with test
        setShowUserDetailsModal(false);
      } else {
        setError('Failed to save user details. Please try again.');
      }
    } catch (error) {
      setError(`Failed to save user details: ${error.message}`);
    } finally {
      setSavingUserDetails(false);
    }
  };

  const handleAnswerSelect = useCallback((questionId, selectedOption) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: selectedOption
    }));
  }, []);

  // Handle object detection violations with 3-strike system
  const handleObjectDetectionViolation = useCallback((violationData) => {
    const newViolations = [...objectDetectionViolations, violationData];
    setObjectDetectionViolations(newViolations);
    
    const violationCount = violationData.count || newViolations.length;
    
    // 3-strike system
    if (violationCount >= 3) {
      // Mark malicious activity and auto-submit with 0 marks
      setMaliciousActivityDetected(true);
      
      // Auto-submit the test
      if (handleSubmitTestRef.current && !hasAutoSubmittedRef.current) {
        hasAutoSubmittedRef.current = true;
        setTimeout(() => {
          handleSubmitTestRef.current();
        }, 1000);
      }
    }
    
    // Log violation to Firebase for admin review
    if (currentUser) {
      // Could save to Firebase for admin review
      console.log('Object detection violation:', violationData);
    }
  }, [currentUser, objectDetectionViolations]);

  // Handle auto-submit from object detection
  const handleAutoSubmitFromDetection = useCallback((message) => {
    setMaliciousActivityDetected(true);
    setShowMaliciousActivityWarning(true);
    // Auto-submit test with 0 marks
    if (handleSubmitTestRef.current) {
      handleSubmitTestRef.current();
    }
  }, []);

  const calculateResults = (questions, answers) => {
    let totalScore = 0;
    let correctAnswers = 0;
    let wrongAnswers = 0;
    let unattempted = 0;
    let detailedResults = [];

    questions.forEach((question, index) => {
      const userAnswer = answers[question.id];
      const isCorrect = checkAnswerCorrectness(question, userAnswer);
      const questionScore = calculateQuestionScore(question, userAnswer, isCorrect);
      
      // Ensure questionScore is a number
      const numericQuestionScore = Number(questionScore) || 0;
      totalScore += numericQuestionScore;
      
      if (userAnswer === undefined || userAnswer === null || userAnswer === '') {
        unattempted++;
      } else if (isCorrect) {
        correctAnswers++;
      } else {
        wrongAnswers++;
      }

      detailedResults.push({
        questionId: question.id,
        questionIndex: index,
        userAnswer,
        correctAnswer: question.correctAnswer,
        isCorrect,
        score: numericQuestionScore,
        timeSpent: questionTimes[question.id] || 0,
        topic: question.topic,
        difficulty: question.difficulty
      });
    });

    // Ensure totalScore is a number and not NaN
    totalScore = Number(totalScore) || 0;
    if (isNaN(totalScore)) {
      totalScore = 0;
    }

    const attempted = correctAnswers + wrongAnswers;
    const totalPossibleMarks = questions.reduce((sum, q) => {
      const markingScheme = q.markingScheme || { correct: 4, incorrect: -1, unattempted: 0 };
      return sum + markingScheme.correct;
    }, 0);
    
    const percentageScore = totalPossibleMarks > 0 ? Math.round((totalScore / totalPossibleMarks) * 100) : 0;
    const accuracy = attempted > 0 ? Math.round((correctAnswers / attempted) * 100) : 0;

    return {
      totalScore,
      percentageScore,
      correctAnswers,
      wrongAnswers,
      unattempted,
      accuracy,
      detailedResults,
      totalTimeSpent: Math.floor((Date.now() - (startTimeRef.current || Date.now())) / 1000)
    };
  };

  const checkAnswerCorrectness = (question, userAnswer) => {
    if (userAnswer === undefined || userAnswer === null || userAnswer === '') {
      return false;
    }

    switch (question.type) {
      case 'multiple-choice':
        if (question.allowMultiple) {
          // Multiple choice - check if arrays match
          if (Array.isArray(question.correctAnswer) && Array.isArray(userAnswer)) {
            return userAnswer.length === question.correctAnswer.length && 
                   userAnswer.every(ans => question.correctAnswer.includes(ans));
          }
          return false;
        } else {
          // Single choice
          return userAnswer === question.correctAnswer;
        }
      
      case 'integer':
      case 'floating-point':
        return parseFloat(userAnswer) === parseFloat(question.correctAnswer);
      
      case 'matrix-match':
        // For matrix match, check if all matches are correct
        if (typeof userAnswer === 'object' && typeof question.correctAnswer === 'object') {
          const userMatches = Object.keys(userAnswer).length;
          const correctMatches = Object.keys(question.correctAnswer).length;
          if (userMatches !== correctMatches) return false;
          
          return Object.keys(userAnswer).every(key => 
            userAnswer[key] === question.correctAnswer[key]
          );
        }
        return false;
      
      case 'assertion-reason':
        return userAnswer === question.correctAnswer;
      
      default:
        return userAnswer === question.correctAnswer;
    }
  };

  const calculateQuestionScore = (question, userAnswer, isCorrect) => {
    const markingScheme = question.markingScheme || { correct: 4, incorrect: -1, unattempted: 0 };
    
    if (userAnswer === undefined || userAnswer === null || userAnswer === '') {
      return markingScheme.unattempted;
    }
    
    return isCorrect ? markingScheme.correct : markingScheme.incorrect;
  };


  const calculateNewAverage = (currentAvg, newScore, currentCount) => {
    return Math.round(((currentAvg * currentCount) + newScore) / (currentCount + 1));
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading test...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Test</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/dashboard/mock-tests')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Tests
          </button>
        </div>
      </div>
    );
  }

  // Show malicious activity warning modal (must be before !isStarted check)
  if (showMaliciousActivityWarning) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-3xl w-full max-h-[90vh] overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg shadow-xl p-6 sm:p-8"
          >
            <div className="text-center mb-6">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                  <Shield className="w-8 h-8 text-red-600" />
                </div>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Important: Test Monitoring & Security
              </h2>
              <p className="text-gray-600">
                Please read the following information carefully before starting the test
              </p>
            </div>

            <div className="space-y-6 mb-6">
              {/* Test Information */}
              <div className="border-b pb-4">
                <h3 className="font-semibold text-gray-900 mb-3">Test Information</h3>
                <div className="text-gray-800 text-sm space-y-2">
                  <p><strong>Test Name:</strong> {testInfo?.name || 'Mock Test'}</p>
                  <p><strong>Total Questions:</strong> {testInfo?.totalQuestions || questions.length || 0}</p>
                  <p><strong>Duration:</strong> {formatTime(testInfo?.duration || 0)}</p>
                </div>
              </div>

              {/* Marking Scheme */}
              <div className="border-b pb-4">
                <h3 className="font-semibold text-gray-900 mb-3">Marking Scheme</h3>
                {(() => {
                  const markingScheme = testInfo?.markingScheme || { correct: 4, incorrect: -1, partial: 0 };
                  const correctMarks = markingScheme.correct || 4;
                  const incorrectMarks = markingScheme.incorrect || -1;
                  const unattemptedMarks = markingScheme.partial !== undefined ? markingScheme.partial : (markingScheme.unattempted !== undefined ? markingScheme.unattempted : 0);
                  return (
                    <div className="text-gray-800 text-sm space-y-1">
                      <p>• <strong>Correct Answer:</strong> {correctMarks > 0 ? '+' : ''}{correctMarks} marks</p>
                      <p>• <strong>Wrong Answer:</strong> {incorrectMarks} mark{Math.abs(incorrectMarks) !== 1 ? 's' : ''}</p>
                      <p>• <strong>Unattempted:</strong> {unattemptedMarks} marks</p>
                      <p className="mt-2 text-xs">Note: Some questions may have different marking schemes as specified in the question.</p>
                    </div>
                  );
                })()}
              </div>

              {/* Question Types */}
              <div className="border-b pb-4">
                <h3 className="font-semibold text-gray-900 mb-3">Question Types</h3>
                {(() => {
                  // Map question type values to their display names and descriptions
                  const questionTypeMap = {
                    'multiple-choice': { name: 'Multiple Choice (Single)', description: 'Select one option from the given choices' },
                    'multiple-choice-multi': { name: 'Multiple Choice (Multiple)', description: 'Select one or more correct options' },
                    'integer': { name: 'Integer Type', description: 'Enter a numerical integer answer' },
                    'floating-point': { name: 'Floating Point', description: 'Enter a decimal number answer' },
                    'matrix-match': { name: 'Matrix Match', description: 'Match items from Column A with Column B' },
                    'assertion-reason': { name: 'Assertion-Reason', description: 'Answer whether assertion and reason are correct' },
                    'subjective': { name: 'Subjective/Essay', description: 'Provide a detailed written answer' }
                  };

                  // Get unique question types from the test's questions
                  // Check multiple sources: questions state, testInfo.originalTestData.questions
                  const questionTypesInTest = new Set();
                  
                  // Check questions state
                  if (questions && questions.length > 0) {
                    questions.forEach(q => {
                      const type = q.type || 'multiple-choice';
                      questionTypesInTest.add(type);
                    });
                  }
                  
                  // Also check testInfo.originalTestData.questions if available
                  if (!questionTypesInTest.size && testInfo?.originalTestData?.questions && testInfo.originalTestData.questions.length > 0) {
                    testInfo.originalTestData.questions.forEach(q => {
                      const type = q.type || 'multiple-choice';
                      questionTypesInTest.add(type);
                    });
                  }

                  // Filter to only show question types that are actually in the test
                  const displayedTypes = Object.entries(questionTypeMap)
                    .filter(([type]) => questionTypesInTest.has(type))
                    .map(([type, info]) => ({ type, ...info }));

                  // If no questions loaded yet, show all types (fallback)
                  if (displayedTypes.length === 0) {
                    return (
                      <div className="text-gray-800 text-sm space-y-1">
                        <p>• <strong>Multiple Choice (Single):</strong> Select one option from the given choices</p>
                        <p>• <strong>Multiple Choice (Multiple):</strong> Select one or more correct options</p>
                        <p>• <strong>Integer Type:</strong> Enter a numerical integer answer</p>
                        <p>• <strong>Floating Point:</strong> Enter a decimal number answer</p>
                        <p>• <strong>Matrix Match:</strong> Match items from Column A with Column B</p>
                        <p>• <strong>Assertion-Reason:</strong> Answer whether assertion and reason are correct</p>
                      </div>
                    );
                  }

                  return (
                    <div className="text-gray-800 text-sm space-y-1">
                      {displayedTypes.map((typeInfo, index) => (
                        <p key={index}>• <strong>{typeInfo.name}:</strong> {typeInfo.description}</p>
                      ))}
                    </div>
                  );
                })()}
              </div>

              {/* Test Interface Buttons */}
              <div className="border-b pb-4">
                <h3 className="font-semibold text-gray-900 mb-3">Test Interface Buttons</h3>
                <div className="text-gray-800 text-sm space-y-3">
                  <div>
                    <p className="font-semibold mb-1">Navigation Buttons (Right Sidebar):</p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><strong>First:</strong> Jump to the first question</li>
                      <li><strong>Prev:</strong> Go to the previous question</li>
                      <li><strong>Next:</strong> Go to the next question</li>
                      <li><strong>Last:</strong> Jump to the last question</li>
                      <li><strong>Question Palette:</strong> Click on any question number to navigate directly to that question</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-semibold mb-1">Action Buttons (Below Question):</p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><strong>Previous:</strong> Navigate to the previous question</li>
                      <li><strong>Clear Response:</strong> Clear your selected answer for the current question</li>
                      <li><strong>Mark for Review:</strong> Mark the question for later review. This button appears as an orange button with a circular arrow icon</li>
                      <li><strong>Submit Test:</strong> Submit your test when completed. This is a green button with a checkmark icon</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-semibold mb-1">Question Status Colors (Question Palette):</p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><strong>Green:</strong> Answered questions</li>
                      <li><strong>Purple:</strong> Marked for Review questions</li>
                      <li><strong>Red:</strong> Flagged questions</li>
                      <li><strong>Blue:</strong> Current question you are viewing</li>
                      <li><strong>Light Grey:</strong> Visited but unanswered questions</li>
                      <li><strong>White:</strong> Unvisited questions</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-semibold mb-1">Question Header Information:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li>Each question shows its number, difficulty level, and time spent on that question</li>
                      <li>The marking scheme (Marks: +4 -1 0) is displayed for each question</li>
                      <li>You can bookmark questions using the bookmark button</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Answer Calculation */}
              <div className="border-b pb-4">
                <h3 className="font-semibold text-gray-900 mb-3">Answer Calculation</h3>
                <div className="text-gray-800 text-sm space-y-2">
                  <p><strong>For Score Calculation:</strong></p>
                  <ul className="list-disc list-inside ml-2 space-y-1">
                    <li><strong>Saved Answers:</strong> All saved answers will be considered for scoring</li>
                    <li><strong>Marked for Review:</strong> Questions marked for review with saved answers will also be considered for scoring</li>
                    <li><strong>Unanswered Questions:</strong> Will be marked as unattempted (0 marks)</li>
                  </ul>
                  <p className="mt-2"><strong>Important:</strong> Your answers are automatically saved when you select them. You can change your answer anytime before submitting the test. Questions marked for review are still counted in your score if they have saved answers.</p>
                </div>
              </div>

              {/* Malicious Activity Detection */}
              <div className="border-b pb-4">
                <h3 className="font-semibold text-gray-900 mb-3">AI-Powered Camera Monitoring & Anti-Cheating</h3>
                <div className="text-gray-800 text-sm space-y-2">
                  <p className="font-semibold text-red-600">⚠️ IMPORTANT: Camera monitoring is active during the test!</p>
                  <p>The system uses AI to detect unauthorized objects in your camera view:</p>
                  <ul className="list-disc list-inside ml-2 space-y-1">
                    <li><strong>Camera - Allowed:</strong> Your face, hands, and pen only</li>
                    <li><strong>Camera - Forbidden:</strong> Phones, tablets, laptops, books, or any other devices</li>
                  </ul>
                  <div className="bg-red-50 border-l-4 border-red-500 p-3 mt-3">
                    <p className="font-semibold text-red-800 mb-2">⚠️ 3-Strike Warning System:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1 text-red-700">
                      <li><strong>1st Violation:</strong> Red warning modal appears</li>
                      <li><strong>2nd Violation:</strong> Second warning - Final chance!</li>
                      <li><strong>3rd Violation:</strong> Test automatically submits with <strong>0 marks</strong></li>
                    </ul>
                    <p className="mt-2 text-red-800 font-semibold">
                      You will see a red bounding box around any forbidden object detected in the camera feed (bottom-right corner).
                    </p>
                  </div>
                  <p className="mt-2"><strong>Please keep only your face, hands, and pen visible in the camera.</strong> Remove all phones, devices, and books from view before starting the test.</p>
                </div>
              </div>

              {/* General Instructions */}
              <div className="pb-4">
                <h3 className="font-semibold text-gray-900 mb-3">General Instructions</h3>
                <div className="text-gray-800 text-sm space-y-2">
                  <ul className="list-disc list-inside space-y-1">
                    <li>Read each question carefully before answering</li>
                    <li>You can navigate between questions using the Previous/Next buttons below the question or the navigation buttons in the right sidebar</li>
                    <li>Use the Question Palette in the right sidebar to see all questions and their status at a glance</li>
                    <li>Click on any question number in the palette to jump directly to that question</li>
                    <li>Use the "Mark for Review" button to flag questions you want to revisit later</li>
                    <li>Your answers are automatically saved when you select them</li>
                    <li>You can change your answers anytime before submitting the test</li>
                    <li>The test will automatically submit when the time expires</li>
                    <li>Review all your answers before final submission using the Question Palette</li>
                    <li>Do not refresh the page or close the browser during the test</li>
                    <li>Stay in fullscreen mode throughout the test</li>
                    <li>Do not switch tabs or minimize the window</li>
                    <li>The right sidebar shows statistics including answered, unanswered, and review counts, as well as time statistics</li>
                  </ul>
                </div>
              </div>

            </div>

            <div className="border-t pt-4 mb-6">
              <p className="text-sm text-gray-800 text-center">
                <strong>By clicking "I Understand & Start Test", you acknowledge:</strong>
              </p>
              <ul className="text-sm text-gray-800 mt-2 space-y-1 text-center">
                <li>Camera monitoring will be active throughout the test</li>
                <li>Only your face, hands, and pen are allowed in camera view. No other objects or devices are permitted.</li>
                <li><strong className="text-red-600">3 violations will result in automatic test submission with 0 marks</strong></li>
                <li>You agree to follow all test rules and guidelines</li>
              </ul>
            </div>

            <div className="flex justify-center space-x-4">
              <button
                onClick={() => {
                  setShowMaliciousActivityWarning(false);
                  navigate('/dashboard/mock-tests');
                }}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleWarningAcknowledged}
                className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium transition-colors flex items-center"
              >
                <Shield className="w-5 h-5 mr-2" />
                I Understand & Start Test
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  // Show test instructions before starting
  if (!isStarted) {
    // Check interface type - default to 'jee' for backward compatibility
    const interfaceType = testData?.interfaceType || testInfo?.interfaceType || 'jee';
    
    // Render CAT interface if specified
    if (interfaceType === 'cat') {
      return (
        <CATInstructions
          testInfo={testInfo}
          onStartTest={startTest}
          userProfile={userProfile}
        />
      );
    }
    
    // Default JEE interface
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-2xl mx-auto p-8">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-4">{testInfo?.name}</h1>
              <div className="flex items-center justify-center space-x-8 text-gray-600">
                <div className="flex items-center">
                  <Clock className="h-5 w-5 mr-2" />
                  <span>{formatTime(testInfo?.duration || 0)}</span>
                </div>
                <div className="flex items-center">
                  <Target className="h-5 w-5 mr-2" />
                  <span>{testInfo?.totalQuestions} Questions</span>
                </div>
              </div>
            </div>

            <div className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Instructions</h2>
              <ul className="space-y-2 text-gray-600">
                {(testInfo?.instructions || []).map((instruction, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>{instruction}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Marking Scheme</h2>
              {(() => {
                const markingScheme = testInfo?.markingScheme || { correct: 4, incorrect: -1, partial: 0 };
                const correctMarks = markingScheme.correct || 4;
                const incorrectMarks = markingScheme.incorrect || -1;
                const unattemptedMarks = markingScheme.partial !== undefined ? markingScheme.partial : (markingScheme.unattempted !== undefined ? markingScheme.unattempted : 0);
                return (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <div className="text-2xl font-bold text-green-600">{correctMarks > 0 ? '+' : ''}{correctMarks}</div>
                      <div className="text-sm text-green-700">Correct Answer</div>
                    </div>
                    <div className="text-center p-4 bg-red-50 rounded-lg">
                      <div className="text-2xl font-bold text-red-600">{incorrectMarks}</div>
                      <div className="text-sm text-red-700">Wrong Answer</div>
                    </div>
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                      <div className="text-2xl font-bold text-gray-600">{unattemptedMarks}</div>
                      <div className="text-sm text-gray-700">Unattempted</div>
                    </div>
                  </div>
                );
              })()}
            </div>

            <div className="flex justify-center">
              <button
                onClick={startTest}
                className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-lg flex items-center"
              >
                <Play className="h-5 w-5 mr-2" />
                Start Test
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show user details modal if required for manual evaluation
  if (showUserDetailsModal) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6"
        >
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            Enter Your Details
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
            This test requires manual evaluation. Please provide your name and email to proceed.
          </p>

          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={userDetails.name}
                onChange={(e) => setUserDetails(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                placeholder="Enter your full name"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={userDetails.email}
                onChange={(e) => setUserDetails(prev => ({ ...prev, email: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                placeholder="Enter your email address"
                required
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={() => {
                setShowUserDetailsModal(false);
                navigate('/dashboard/mock-tests');
              }}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              disabled={savingUserDetails}
            >
              Cancel
            </button>
            <button
              onClick={handleUserDetailsSubmit}
              disabled={savingUserDetails || !userDetails.name.trim() || !userDetails.email.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {savingUserDetails ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Continue'
              )}
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  // Proctoring is handled via camera monitoring (explained in warning modal)
  // No separate proctoring setup needed

  // Show summary table for manual evaluation mode
  if (isCompleted && isManualEvaluation && testData) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-6">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Test Submitted Successfully
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Your answers have been saved and will be evaluated by the admin. You will receive your results once the evaluation is complete.
            </p>
          </div>
          
          <TestSummaryTable testData={testData} userAnswers={answers} />
          
          <div className="mt-6 flex justify-center">
            <button
              onClick={() => navigate('/dashboard/mock-tests')}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors"
            >
              Back to Mock Tests
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Check interface type - default to 'jee' for backward compatibility
  const interfaceType = testData?.interfaceType || testInfo?.interfaceType || 'jee';

  return (
    <>

      
      {/* Render CAT interface if specified, otherwise default JEE interface */}
      {interfaceType === 'cat' ? (
        <CATTestInterface
          questions={questions}
          answers={answers}
          onAnswerSelect={handleAnswerSelect}
          onTestSubmit={handleSubmitTest}
          testInfo={testInfo}
          timeLeft={timeLeft}
          isSubmitting={isSubmitting}
          questionTimes={questionTimes}
          autoSaveEnabled={autoSaveEnabled}
          onAutoSaveToggle={() => setAutoSaveEnabled(!autoSaveEnabled)}
          sessionId={sessionId}
          testId={testId}
          onQuestionChange={(index, questionId) => setCurrentQuestionIndex(index)}
        />
      ) : (
        <EnhancedTestInterface
          questions={questions}
          answers={answers}
          onAnswerSelect={handleAnswerSelect}
          onTestSubmit={handleSubmitTest}
          testInfo={testInfo}
          timeLeft={timeLeft}
          isSubmitting={isSubmitting}
          questionTimes={questionTimes}
          autoSaveEnabled={autoSaveEnabled}
          onAutoSaveToggle={() => setAutoSaveEnabled(!autoSaveEnabled)}
          sessionId={sessionId}
          testId={testId}
          onQuestionChange={(index, questionId) => setCurrentQuestionIndex(index)}
        />
      )}
      
      {/* Camera Monitor - Active during test */}
      {isStarted && !isCompleted && (
        <CameraMonitor
          onViolation={handleObjectDetectionViolation}
          sessionId={sessionId}
        />
      )}
    </>
  );
};

export default TestInterface;
